import { decodeName, decodeNameList } from './decodeName';
import { copyText, downloadText, slugifyFilename } from './download';
import { buildLeafSeoIndex } from './leafSeoIndex';
import { buildTableFromTree } from './recluster';
import { mapIntent, mapPurpose } from './seoMetrics';
import type {
  ClusterData,
  ClusterTableRow,
  HierarchicalNode
} from '../types';

function decodeTree(node: HierarchicalNode | null | undefined): HierarchicalNode | null {
  if (!node) return null;
  const next: HierarchicalNode = {
    name: decodeName(node.name)
  };

  if (!node.children?.length) {
    next.search_volume_miztols = node.search_volume_miztols ?? 0;
    next.difficulty = node.difficulty ?? null;
    next.len = node.len ?? null;
    next.intent = node.intent ?? [];
    next.purpose = node.purpose ?? [];
  }

  if (node.children?.length) {
    next.children = node.children
      .map(decodeTree)
      .filter((child): child is HierarchicalNode => child != null);
  }
  return next;
}

function collectLeaves(
  node: HierarchicalNode | null | undefined,
  list: string[] = []
): string[] {
  if (!node) return list;
  if (!node.children?.length) {
    list.push(node.name);
    return list;
  }
  node.children.forEach(child => collectLeaves(child, list));
  return list;
}

function escapeCsv(value: unknown): string {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function getTableRows(data: ClusterData | null | undefined): ClusterTableRow[] {
  if (Array.isArray(data?.table) && data.table.length) return data.table;
  return buildTableFromTree(data?.hierarchical);
}

export function buildDecodedCluster(data: ClusterData) {
  const table = getTableRows(data);
  return {
    task_id: data.task_id,
    user_id: data.user_id,
    stem: decodeName(data.stem),
    type_clustering: data.type_clustering,
    created_at: data.created_at,
    hierarchical: decodeTree(data.hierarchical),
    table: table.map(row => ({
      name: decodeNameList(row.name ?? [])
    })),
    keywords: collectLeaves(decodeTree(data.hierarchical))
  };
}

const EXPORT_BRAND_PREFIX = 'mizfa-tools-cluster';

export function getExportBaseName(data: ClusterData | null | undefined): string {
  const stem = slugifyFilename(
    decodeName(data?.stem),
    data?.task_id ? String(data.task_id).slice(0, 12) : 'export'
  );
  return `${EXPORT_BRAND_PREFIX}-${stem}`;
}

export function exportDecodedJson(data: ClusterData): string {
  const payload = buildDecodedCluster(data);
  const base = getExportBaseName(data);
  downloadText(
    JSON.stringify(payload, null, 2),
    `${base}.json`,
    'application/json;charset=utf-8'
  );
  return `${base}.json`;
}

function joinLabels(values: string[] = []): string {
  return values.filter(Boolean).join('، ');
}

/** One Excel row per keyword path, matching table SEO columns. */
export function buildTableExcelRows(
  data: ClusterData | null | undefined,
  rows?: ClusterTableRow[]
): Record<string, unknown>[] {
  const tableRows = rows?.length ? rows : getTableRows(data);
  const leafSeo = buildLeafSeoIndex(data?.hierarchical);
  const maxDepth = tableRows.reduce(
    (max, row) => Math.max(max, row.name?.length ?? 0),
    0
  );

  return tableRows.map(row => {
    const path = decodeNameList(row.name ?? []);
    const leaf = path[path.length - 1] || '';
    const seo = leafSeo.get(leaf);
    const record: Record<string, unknown> = {};

    for (let i = 0; i < maxDepth; i += 1) {
      record[`سطح ${i + 1}`] = path[i] ?? '';
    }

    record['میزان جستجو'] = seo?.search_volume_miztols ?? '';
    record['سختی'] = seo?.difficulty ?? '';
    record['طول'] = seo?.len ?? '';
    record['نیت'] = joinLabels(
      (seo?.intent || []).map(code => mapIntent(code).label)
    );
    record['هدف'] = joinLabels((seo?.purpose || []).map(item => mapPurpose(item)));
    return record;
  });
}

export function exportPathsCsv(data: ClusterData): string {
  const records = buildTableExcelRows(data);
  const headers = records[0]
    ? Object.keys(records[0])
    : ['سطح 1', 'میزان جستجو', 'سختی', 'طول', 'نیت', 'هدف'];
  const lines = [
    headers.join(','),
    ...records.map(record =>
      headers.map(header => escapeCsv(record[header])).join(',')
    )
  ];
  // BOM for Excel RTL/Persian
  const content = `\uFEFF${lines.join('\n')}`;
  const base = getExportBaseName(data);
  downloadText(content, `${base}-table.csv`, 'text/csv;charset=utf-8');
  return `${base}-table.csv`;
}

export function exportKeywordsTxt(data: ClusterData): string {
  const decoded = buildDecodedCluster(data);
  const content = decoded.keywords.join('\n');
  const base = getExportBaseName(data);
  downloadText(content, `${base}-keywords.txt`);
  return `${base}-keywords.txt`;
}

export async function copyKeywords(data: ClusterData): Promise<number> {
  const decoded = buildDecodedCluster(data);
  await copyText(decoded.keywords.join('\n'));
  return decoded.keywords.length;
}

export async function copySelectedPath(path: string[] = []): Promise<string> {
  const text = path.join(' › ');
  await copyText(text);
  return text;
}
